/**
 * Calculate `offset` needed to get paginated results.
 *
 * @example
 *  To get results from the 1st page with 10 items on a page,
 *  a URL must be extended with "?limit=10&offset=0".
 *  "?limit=10&offset=10" for the 2nd page.
 *  "?limit=10&offset=20" for the 3rd page etc.
 *
 * @param {Number} pageNumber
 * @param {Number} itemsOnPage
 */
export function getOffset(pageNumber, itemsOnPage) {
  return (pageNumber - 1) * itemsOnPage;
}


/**
 * Return promise that returns object with paginated results.
 * The structure of returned object:
 * {
 *   count: Items qty {Number}.
 *   next: URL of the next page {String} or {null}.
 *   previous: URL of the previous page {String} or {null}.
 *   results: {Array of Objects}.
 * }
 *
 * @param {Function} serviceToCall - E.g. `getUsers`, `getTeams`.
 * @param {Object} payload
 * @return {Promise<{count, next, previous, results}>}
 */
export function getItemsPaginated(serviceToCall, payload) {
  const { pageNumber, itemsOnPage, ...otherQueryParams } = payload;
  const offset = getOffset(pageNumber, itemsOnPage);
  const updatedQueryParams = convertObjectKeysToSnakeCase(otherQueryParams);
  return serviceToCall({ limit: itemsOnPage, offset, ...updatedQueryParams })
}


/**
 * Convert "snake case" string to "camel case" string.
 *
 * @example "product_title" -> "productTitle"
 *
 * @param {String} s
 * @returns {String} Converted string
 */
export function snakeCaseToCamelCase(s) {
  return s.replace(/([-_][a-z])/ig, ($1) => {
    return $1.toUpperCase().replace('_', '');
  });
}


/**
 * Convert "camel case" string to "snake case" string.
 *
 * @example "productTitle" -> "product_title"
 *
 * @param {String} s
 * @returns {String} Converted string
 */
export function camelCaseToSnakeCase(s) {
  return s.replace(/[\w]([A-Z])/g, (m) => {
    return m[0] + '_' + m[1];
  }).toLowerCase();
}


/**
 * Extend object with keys from `keyArray` converted to "camel case".
 *
 * @param {Object} obj
 * @param {Array} keyArray
 * @returns {Object} Updated object
 */
export function objectKeysToCamelCase(obj, keyArray) {
  for (let key of keyArray) {
    if (obj[key]) {
      const camelCaseKey = snakeCaseToCamelCase(key);
      obj[camelCaseKey] = obj[key];
    }
  }
  return obj;
}

/**
 * Convert all object's keys from "camel case" to "snake case".
 *
 * @example
 * { teamId: 12, 'productTitle': 'Title' } -> { team_id: 12, product_title: 'Title' }
 *
 * @param {Object} obj
 * @returns {Object} Updated object
 */
export function convertObjectKeysToSnakeCase(obj) {
  for (let key of Object.keys(obj)) {
    const snakeCaseKey = camelCaseToSnakeCase(key);
    if (snakeCaseKey !== key) {
      obj[snakeCaseKey] = obj[key];
      delete obj[key];
    }
  }
  return obj;
}

/**
 * Extend URL params with data from query string.
 *
 * @param route
 */
export function addQueryToProps(route) {
  const props = { ...route.params };
  props.query = { ...route.query };
  return props;
}



// WEBPACK FOOTER //
// ./src/services/common.js